import type { FigureKind, TFigureCaptionElement } from '@alpod-ai-writer/plate-plugins/figure-caption';
import { FigureCaptionPlugin } from '@alpod-ai-writer/plate-plugins/figure-caption/react';
import { cn } from '@udecode/cn';
import { PlateElement, useEditorPlugin, withRef } from '@udecode/plate/react';
import { ChartLineIcon, ImageIcon, MapIcon, PencilLineIcon, RadicalIcon, TableIcon } from 'lucide-react';

import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger, useOpenState } from '../common/dropdownMenu.component';

const dropdownMenuItems = [
    {
        icon: <ImageIcon />,
        label: 'Popisek k obrázku',
        value: 'image',
    },
    {
        icon: <TableIcon />,
        label: 'Popisek k tabulce',
        value: 'table',
    },
    {
        icon: <ChartLineIcon />,
        label: 'Popisek ke grafu',
        value: 'chart',
    },
    {
        icon: <RadicalIcon />,
        label: 'Popisek ke vzorci',
        value: 'formula',
    },
    {
        icon: <MapIcon />,
        label: 'Popisek k mapě',
        value: 'map',
    },
    {
        icon: <PencilLineIcon />,
        label: 'Vlastní druh popisku',
        value: 'other',
    }
];

export const FigureCaptionElement = withRef<
  typeof PlateElement,
  {}
>(({ children, className, ...props }, ref) => {
    const docEditor = useDocumentEditor();
    const docConfig = docEditor.template.config;
    const element = props.element as TFigureCaptionElement;
    const { align, figureKind } = element;
    const editor = useEditorPlugin(FigureCaptionPlugin);
    const openState = useOpenState();

    const captionPositionOption = editor.getOption('captionPosition');
    const captionPosition = captionPositionOption[figureKind] ?? docConfig.captionsPosition;

    return (
        <PlateElement
            ref={ref}
            as='p'
            className={cn(
                className,
                'flex flex-row items-start gap-1 bg-gray-100 p-1 rounded-md',
                captionPosition === 'below' && 'mb-4',
                align === 'left' && 'text-left justify-start',
                align === 'center' && 'text-center justify-center',
                align === 'right' && 'text-right justify-end',
                align === 'justify' && 'text-justify justify-start'
            )}
            {...props}
        >
            <DropdownMenu modal={false} {...openState}>
                <DropdownMenuTrigger asChild>
                    <button
                        className='flex flex-row items-center gap-1 flex-shrink-0 bg-transparent hover:bg-blue-200 hover:text-gray-900 rounded p-1 text-sm transition-colors duration-200 text-gray-600'
                        contentEditable={false}
                    >
                        {figureKind === 'image' && (
                            <><ImageIcon size={16} /> Obrázek</>
                        )}
                        {figureKind === 'table' && (
                            <><TableIcon size={16} /> Tabulka</>
                        )}
                        {figureKind === 'chart' && (
                            <><ChartLineIcon size={16} /> Graf</>
                        )}
                        {figureKind === 'formula' && (
                            <><RadicalIcon size={16} /> Vzorec</>
                        )}
                        {figureKind === 'map' && (
                            <><MapIcon size={16} /> Mapa</>
                        )}
                        {figureKind === 'other' && (
                            <><PencilLineIcon size={16} /></>
                        )}
                    </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                    className="ignore-click-outside/toolbar min-w-0"
                    onCloseAutoFocus={(e) => {
                        e.preventDefault();
                        editor.tf.focus();
                    }}
                    align="start"
                >
                    <DropdownMenuRadioGroup
                        value={figureKind}
                        onValueChange={(value: string) => {
                            editor.tf.figureCaption.setKind(element, value as FigureKind);
                        }}
                    >
                        {dropdownMenuItems.map(({ icon, label, value: itemValue }) => (
                            <DropdownMenuRadioItem
                                key={itemValue}
                                className="min-w-[180px]"
                                value={itemValue}
                            >
                                {icon}
                                {label}
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
            <span className='inline-block p-0.5'>
                {children}
            </span>
            {align === 'center' && (
                <span className='inline-block w-4' />
            )}
        </PlateElement>
    );
});
